import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { datadogRum } from '@datadog/browser-rum';
import { Skeleton } from '@mui/material';
import Button from '@mui/material/Button';
import { RadioGroupOptions, RadioGroupVariants } from '@sage/ui-components';

import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import { useAnalytics } from 'src/AnalyticsContext';
import ListLoader from 'src/components/fragments/ListLoader';
import ShiftCard from 'src/components/user-shift/ShiftCard';
import useShiftInstances from 'src/hooks/useShiftInstances';
import {
    selectFacilityId,
    selectFacilityOnCallState,
    selectSelectedShiftRid,
    selectStaffMemberId,
    setSelectedShiftRid,
} from 'src/store/configSlice';
import { useUpdateOnCallStateMutation } from 'src/store/sageAdminApi';

const SelectShiftDrawer = (props: DrawerComponentProps) => {
    const { onNext, onClose } = props;
    const dispatch = useDispatch();

    const currentShiftRid = useSelector(selectSelectedShiftRid);
    const facilityId = useSelector(selectFacilityId);
    const staffMemberId = useSelector(selectStaffMemberId);
    const facilityOnCallState = useSelector(selectFacilityOnCallState);
    const { shiftInstances, isLoadingShifts } = useShiftInstances();
    const [newShiftRid, setNewShiftRid] = useState(currentShiftRid);
    const { trackEvent } = useAnalytics();
    const [updateOnCallState] = useUpdateOnCallStateMutation();

    const handleConfirm = async () => {
        if (!newShiftRid) return;

        trackEvent('Shift Selection Confirmed', {
            pageName: 'Login Step One',
            shiftId: newShiftRid,
        });

        dispatch(setSelectedShiftRid(newShiftRid));

        if (facilityOnCallState?.isOnCall) {
            try {
                await updateOnCallState({
                    staffMemberId,
                    facilityId,
                    updateOnCallStateRequest: {
                        isOnCall: true,
                        companyPhoneId: facilityOnCallState.companyPhoneId,
                        shiftInstanceId: newShiftRid,
                    },
                }).unwrap();
            } catch (error) {
                datadogRum.addError(error, {
                    context: 'SelectShiftDrawer shift persistence failed',
                    staffMemberId,
                    facilityId,
                    shiftInstanceId: newShiftRid,
                });
            }
        }

        onNext();
    };

    const handleSelectOption = (optionId: string) => {
        trackEvent('Shift Selected', {
            pageName: 'Login Step One',
            shiftId: optionId,
        });

        setNewShiftRid(optionId);
    };

    return (
        <>
            <DrawerTitle title="Select Shift" />

            <DrawerBody sx={{ gap: 2 }}>
                {isLoadingShifts && (
                    <ListLoader
                        numItems={3}
                        component={() => <Skeleton variant="rounded" height={70} />}
                    />
                )}
                {!isLoadingShifts && (
                    <RadioGroupOptions
                        options={shiftInstances.map((shift) => ({
                            value: shift.id || '',
                            label: <ShiftCard key={shift.id} shift={shift} />,
                        }))}
                        variant={RadioGroupVariants.Outlined}
                        onSelectOption={(optionId: string) => handleSelectOption(optionId)}
                        selectedOption={newShiftRid}
                    />
                )}
            </DrawerBody>

            <DrawerFooter sx={{ gap: 2 }}>
                <Button variant="contained" onClick={handleConfirm}>
                    Confirm Selection
                </Button>
                <Button onClick={onClose}>Cancel</Button>
            </DrawerFooter>
        </>
    );
};

export default SelectShiftDrawer;
